import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL, getAuthHeaders, fetchIngestionSettingsSourcesPage, wipeIngestionGroupData } from '../utils/api';
import { useGroup } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';
import IngestionGroupEditor from './IngestionGroupEditor';
import PostgresIngestWizard from './PostgresIngestWizard';
import HorizonAssignmentModal from './HorizonAssignmentModal';
import '../styles/IngestionCRUD.css';

const SOURCES_PAGE_SIZE = 50;
const EMPTY_GROUP_INITIAL = {};

/**
 * IngestionCRUD - CRUD interface for ingestion sources
 * Displays all ingestion groups across all horizons (not filtered by current horizon)
 * No "Ingest" button - just add, edit, delete functionality
 */
const IngestionCRUD = () => {
  const { user } = useAuth();
  const { activeSuffix } = useGroup();
  const [horizonScope, setHorizonScope] = useState('all');
  /** Draft text in the name field (applied only after Search) */
  const [nameQuery, setNameQuery] = useState('');
  /** Last applied name filter sent to the server */
  const [appliedNameFilter, setAppliedNameFilter] = useState('');
  const [ingestionGroups, setIngestionGroups] = useState([]);
  const [availableHorizons, setAvailableHorizons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [editingGroup, setEditingGroup] = useState(null);
  const [postgresWizardOpen, setPostgresWizardOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [wipingGroup, setWipingGroup] = useState(null);
  const [wipeSchema, setWipeSchema] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState('');
  const [wiping, setWiping] = useState(false);

  const WIPE_CONFIRM_WORD = 'DELETE';
  const wipeConfirmReady = wipeConfirmText === WIPE_CONFIRM_WORD;
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningGroup, setAssigningGroup] = useState(null);
  const [unassignConfirm, setUnassignConfirm] = useState(null);
  const [unassigning, setUnassigning] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageRef = useRef(1);

  // Single server round-trip per page: groups, horizon associations (batched), schedule fields from group rows.
  // `nameContainsForRequest`: when passed (incl. ''), use it for this fetch; when undefined, use `appliedNameFilter`.
  const loadIngestionGroups = useCallback(async (targetPage, nameContainsForRequest) => {
    const p = targetPage != null ? targetPage : pageRef.current;
    const effectiveName =
      nameContainsForRequest === undefined
        ? appliedNameFilter
        : String(nameContainsForRequest).trim();
    setLoading(true);
    setError(null);
    try {
      const fetchOpts = {
        horizonScope,
        ...(activeSuffix ? { activeSuffix } : {}),
        ...(effectiveName ? { nameContains: effectiveName } : {}),
      };
      const data = await fetchIngestionSettingsSourcesPage(p, SOURCES_PAGE_SIZE, fetchOpts);
      const horizons = data.horizons || [];
      const items = data.items || [];
      if (horizons.length === 0 && items.length === 0 && !activeSuffix) {
        setError('No horizons available. Ask an administrator to assign horizons to your account.');
        setAvailableHorizons([]);
        setIngestionGroups([]);
        setTotal(0);
        setPage(1);
        pageRef.current = 1;
        setLoading(false);
        return;
      }

      setAvailableHorizons(horizons);
      const newPage = data.page ?? p;
      setPage(newPage);
      pageRef.current = newPage;
      setTotal(typeof data.total === 'number' ? data.total : items.length);

      if (items.length === 0 && newPage > 1) {
        await loadIngestionGroups(newPage - 1, effectiveName);
        return;
      }

      setIngestionGroups(items);
    } catch (err) {
      console.error('Error loading ingestion groups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [horizonScope, activeSuffix, appliedNameFilter]);

  const handleNameSearch = () => {
    const q = nameQuery.trim();
    setAppliedNameFilter(q);
    pageRef.current = 1;
    loadIngestionGroups(1, q);
  };

  const handleNameReset = () => {
    setNameQuery('');
    setAppliedNameFilter('');
    pageRef.current = 1;
    loadIngestionGroups(1, '');
  };

  useEffect(() => {
    if (horizonScope === 'active' && !activeSuffix) {
      return;
    }
    pageRef.current = 1;
    loadIngestionGroups(1);
  }, [loadIngestionGroups, user?.id, horizonScope, activeSuffix]);

  const handleCreateClick = () => {
    setEditingGroup(null);
    setEditorMode('create');
    setEditorOpen(true);
  };

  const handleEditClick = (group) => {
    setEditingGroup(group);
    setEditorMode('edit');
    setEditorOpen(true);
  };

  const handleDeleteClick = (group) => {
    setDeletingGroup(group);
    setDeleteConfirmOpen(true);
  };

  const handleAssignClick = (group) => {
    console.log('Assign clicked for group:', group);
    setAssigningGroup(group);
    setAssignModalOpen(true);
  };

  const handleAssignClose = () => {
    setAssignModalOpen(false);
    setAssigningGroup(null);
  };

  const handleAssignSaved = () => {
    loadIngestionGroups();
  };

  const canonicalSuffix = (group) =>
    group?._canonicalSuffix || group?._suffix || availableHorizons[0]?.suffix;

  const horizonLabel = (suffix) => {
    const h = availableHorizons.find((x) => x.suffix === suffix);
    return h?.description || h?.suffix || suffix;
  };

  const canDeleteSource = (group) => {
    const hasVisible =
      Array.isArray(group?._associatedHorizons) && group._associatedHorizons.length > 0;
    return !hasVisible && !group?._hasRestrictedAssignments;
  };

  const deleteDisabledTitle = (group) => {
    const assigned = Array.isArray(group?._associatedHorizons) ? group._associatedHorizons : [];
    if (assigned.length > 0) {
      const names = assigned.map((h) => horizonLabel(h)).join(', ');
      return `Assigned to: ${names}. You should unassign these using the Assign button before deleting.`;
    }
    if (group?._hasRestrictedAssignments) {
      return 'This source is also assigned to horizons you cannot view. Remove all horizon assignments before deleting.';
    }
    return 'Remove all horizon assignments before deleting this source';
  };

  const supportsSchemaWipe = (group) => {
    const cfg = group?.config || {};
    const type = String(cfg.type || '').toLowerCase();
    if (type === 'api_source') {
      return Boolean(
        cfg.api_json_columns?.columns?.length
        || cfg.api_source?.auto_generate_columns_from_json
        || cfg.auto_generate_columns_from_json
      );
    }
    if (type === 'google_drive_folder') {
      return Boolean(cfg.directory_depth?.columns?.length || cfg.directory_depth?.enabled);
    }
    return false;
  };

  const closeWipeConfirm = () => {
    if (wiping) return;
    setWipeConfirmOpen(false);
    setWipingGroup(null);
    setWipeSchema(false);
    setWipeConfirmText('');
  };

  const handleWipeClick = (group) => {
    setWipingGroup(group);
    setWipeSchema(false);
    setWipeConfirmText('');
    setWipeConfirmOpen(true);
  };

  const handleWipeConfirm = async () => {
    const suffix = canonicalSuffix(wipingGroup);
    if (!wipingGroup || !suffix) return;

    setWiping(true);
    const groupName = wipingGroup.name || wipingGroup.group_slug;
    try {
      const counts = await wipeIngestionGroupData(suffix, wipingGroup.group_slug, {
        wipeSchema: wipeSchema && supportsSchemaWipe(wipingGroup),
      });
      setError(null);
      const deleted = counts.entriesDeleted ?? 0;
      const schemaNote = counts.schemaWiped
        ? ` Dynamic schema cleared (${counts.schemaFieldsDeleted ?? 0} field(s)).`
        : '';
      closeWipeConfirm();
      await loadIngestionGroups();
      setNotice({
        type: 'success',
        text: `Wiped ${deleted} entr${deleted === 1 ? 'y' : 'ies'} for "${groupName}".${schemaNote}`,
      });
    } catch (err) {
      console.error('Error wiping ingestion data:', err);
      setError(err.message || 'Failed to wipe ingestion data');
      closeWipeConfirm();
    } finally {
      setWiping(false);
    }
  };

  const handleUnassignHorizonClick = (group, horizonSuffix) => {
    setUnassignConfirm({ group, horizonSuffix });
  };

  const handleUnassignConfirm = async () => {
    const { group, horizonSuffix } = unassignConfirm || {};
    const suffix = canonicalSuffix(group);
    if (!group?.group_slug || !suffix || !horizonSuffix) return;

    setUnassigning(true);
    try {
      const headers = await getAuthHeaders();
      const delResp = await fetch(
        `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(group.group_slug)}/horizons/${encodeURIComponent(horizonSuffix)}`,
        { method: 'DELETE', headers }
      );
      const delData = await delResp.json();
      if (!delResp.ok || !delData.success) {
        throw new Error(delData.message || delData.error || 'Failed to remove horizon assignment');
      }
      if (delData.removed === false) {
        throw new Error('Horizon assignment not found or could not be removed');
      }
      setUnassignConfirm(null);
      loadIngestionGroups();
    } catch (err) {
      console.error('Error unassigning horizon:', err);
      setError(err.message);
      setUnassignConfirm(null);
    } finally {
      setUnassigning(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const suffix = canonicalSuffix(deletingGroup);
    if (!deletingGroup || !suffix) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(deletingGroup.group_slug)}`,
        { method: 'DELETE', headers }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete ingestion group');
      }
      setDeleteConfirmOpen(false);
      setDeletingGroup(null);
      loadIngestionGroups();
    } catch (err) {
      console.error('Error deleting ingestion group:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingGroup(null);
  };

  const handleEditorSaved = () => {
    setEditorOpen(false);
    setEditingGroup(null);
    loadIngestionGroups();
  };

  return (
    <div className="ingestion-crud-container">
      <div className="ingestion-crud-header">
        <h3>
          <i className="fas fa-cloud-upload-alt"></i> Ingestion Sources
        </h3>
        <p className="description">
          Each row is one ingestion source. Data horizons it is assigned to appear as tags — use × on a tag to unassign.
          Delete is only available when no horizons are attached.
        </p>
        <button className="btn btn-primary" onClick={handleCreateClick}>
          <i className="fas fa-plus"></i> Add Ingestion Source
        </button>
        <button
          className="btn btn-secondary"
          style={{ marginLeft: 8 }}
          onClick={() => setPostgresWizardOpen(true)}
          disabled={!activeSuffix}
          title={activeSuffix ? 'Create planes from an external Postgres database' : 'Select an active horizon first'}
        >
          <i className="fas fa-database"></i> Ingest from database
        </button>
      </div>

      <div className="ingestion-sources-filters" role="search" aria-label="Ingestion source filters">
        <div className="ingestion-sources-filter-primary">
          <label htmlFor="ingestion-horizon-scope">Scope</label>
          <select
            id="ingestion-horizon-scope"
            className="form-control ingestion-sources-select"
            value={horizonScope}
            onChange={(e) => setHorizonScope(e.target.value)}
          >
            <option value="active">Show by active horizon</option>
            <option value="all">Show all horizons</option>
            <option value="unassigned">Show unassigned ingestion</option>
          </select>
          {horizonScope === 'active' && (
            <span className="ingestion-sources-active-hint" title="From your current horizon selection">
              {activeSuffix ? (
                <>
                  <i className="fas fa-bullseye" aria-hidden /> Active horizon: <code>{activeSuffix}</code>
                </>
              ) : (
                <span className="text-muted">Loading active horizon…</span>
              )}
            </span>
          )}
        </div>
        <div className="ingestion-sources-filter-secondary">
          <label htmlFor="ingestion-name-filter">Filter by name</label>
          <div className="ingestion-sources-name-row">
            <input
              id="ingestion-name-filter"
              className="form-control ingestion-sources-name-input"
              type="search"
              placeholder="Name, slug, or horizon label…"
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleNameSearch();
                }
              }}
              autoComplete="off"
            />
            <div className="ingestion-sources-name-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleNameSearch}
              >
                <i className="fas fa-search" aria-hidden /> Search
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleNameReset}
              >
                <i className="fas fa-undo" aria-hidden /> Reset
              </button>
            </div>
          </div>
          <span className="ingestion-sources-filter-hint">
            Searches name, slug, horizon label, suffix, and assigned horizons (server-side). Click Search to apply.
          </span>
        </div>
      </div>

      {loading && (
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Loading ingestion sources...
        </div>
      )}

      {notice && (
        <div className={`info-message ingestion-notice-${notice.type}`}>
          <i className={`fas fa-${notice.type === 'success' ? 'check-circle' : 'info-circle'}`} /> {notice.text}
        </div>
      )}

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i> {error}
        </div>
      )}

      {!loading && !error && total === 0 && !appliedNameFilter && (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <p>No ingestion sources found</p>
          {horizonScope === 'all' ? (
            <p className="empty-state-subtitle">Click &quot;Add Ingestion Source&quot; to create your first source</p>
          ) : (
            <p className="empty-state-subtitle">Nothing matches this scope. Try &quot;Show all horizons&quot; or another filter.</p>
          )}
        </div>
      )}

      {!loading && !error && total === 0 && appliedNameFilter && (
        <div className="empty-state ingestion-sources-name-empty">
          <i className="fas fa-filter"></i>
          <p>No sources match “{appliedNameFilter}”</p>
          <p className="empty-state-subtitle">Try different words or clear the name filter</p>
        </div>
      )}

      {!loading && !error && total > 0 && (
        <div className="ingestion-groups-grid">
          {ingestionGroups.map((group) => (
            <div key={group.group_slug} className="ingestion-group-card">
              <div className="card-header">
                <h4>{group.name}</h4>
                <span className="group-slug">{group.group_slug}</span>
                {group._schedule?.schedule_enabled && (
                  <span className={`schedule-indicator schedule-indicator-${group._schedule.schedule_status || 'inactive'}`}>
                    <i className={`fas ${
                      group._schedule.schedule_status === 'active' ? 'fa-play-circle' :
                      group._schedule.schedule_status === 'paused' ? 'fa-pause-circle' :
                      group._schedule.schedule_status === 'error' ? 'fa-exclamation-circle' :
                      'fa-clock'
                    }`}></i>
                    {' '}
                    {group._schedule.schedule_status === 'active' ? 'Scheduled' :
                     group._schedule.schedule_status === 'paused' ? 'Paused' :
                     group._schedule.schedule_status === 'error' ? 'Error' : 'Inactive'}
                    {group._schedule.schedule_period_type && (
                      <span className="schedule-period-text">
                        {' '}({group._schedule.schedule_period_value}{
                          group._schedule.schedule_period_type === 'minutes' ? 'min' :
                          group._schedule.schedule_period_type === 'hours' ? 'h' :
                          group._schedule.schedule_period_type === 'days' ? 'd' :
                          group._schedule.schedule_period_type === 'weekly' ? 'w' :
                          group._schedule.schedule_period_type === 'monthly' ? 'mo' : ''
                        })
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="card-body">
                <div className="config-row">
                  <div className="config-item">
                    <strong>Type:</strong>
                    <span>{group.config?.type || 'none'}</span>
                  </div>
                  <div className="config-item">
                    <strong>Auto-Approve:</strong>
                    <span style={{ 
                      color: group.auto_approve ? '#ff9800' : 'var(--text-secondary)',
                      fontWeight: group.auto_approve ? 'bold' : 'normal'
                    }}>
                      {group.auto_approve ? (
                        <><i className="fas fa-check-circle"></i> Enabled</>
                      ) : (
                        'Disabled'
                      )}
                    </span>
                  </div>
                  <div className="config-item">
                    <strong>Auto-Embed:</strong>
                    <span style={{ 
                      color: group.auto_embed ? '#2196f3' : 'var(--text-secondary)',
                      fontWeight: group.auto_embed ? 'bold' : 'normal'
                    }}>
                      {group.auto_embed ? (
                        <><i className="fas fa-vector-square"></i> Enabled</>
                      ) : (
                        'Disabled'
                      )}
                    </span>
                  </div>
                  <div className="config-item">
                    <strong>Privacy:</strong>
                    <span style={{
                      color: group.is_private ? '#9c27b0' : 'var(--text-secondary)',
                      fontWeight: group.is_private ? 'bold' : 'normal'
                    }}>
                      {group.is_private ? (
                        <><i className="fas fa-lock"></i> Private</>
                      ) : (
                        <><i className="fas fa-globe"></i> Public</>
                      )}
                    </span>
                  </div>
                  {group.config?.type === 'google_spreadsheet' && group.config?.spreadsheet_id && (
                    <div className="config-item">
                      <strong>Spreadsheet ID:</strong>
                      <span className="config-value">{group.config.spreadsheet_id}</span>
                    </div>
                  )}
                  {group.config?.type === 'google_drive_folder' && group.config?.folder_id && (
                    <div className="config-item">
                      <strong>Folder ID:</strong>
                      <span className="config-value">{group.config.folder_id}</span>
                    </div>
                  )}
                  {group.config?.type === 'api_source' && group.config?.api_source?.provider && (
                    <div className="config-item">
                      <strong>Provider:</strong>
                      <span className="config-value">{group.config.api_source.provider}</span>
                    </div>
                  )}
                  {group._schedule?.schedule_enabled && group._schedule?.last_ingestion_at && (
                    <div className="config-item">
                      <strong>Last run:</strong>
                      <span style={{ fontSize: '0.88em', color: 'var(--secondary-text-color)' }}>
                        {new Date(group._schedule.last_ingestion_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                  {group._schedule?.schedule_enabled && group._schedule?.next_ingestion_at && (
                    <div className="config-item">
                      <strong>Next run:</strong>
                      <span style={{ fontSize: '0.88em', color: 'var(--secondary-text-color)' }}>
                        {new Date(group._schedule.next_ingestion_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                </div>
                <div className="horizons-and-actions-row">
                  <div className="horizons-section">
                    <strong>Data horizons</strong>
                    <div className="horizon-tags">
                      {group._associatedHorizons && group._associatedHorizons.length > 0 ? (
                        group._associatedHorizons.map((h) => (
                          <span key={h} className="horizon-badge horizon-badge--removable">
                            <span className="horizon-badge-label" title={h}>
                              {horizonLabel(h)}
                            </span>
                            <button
                              type="button"
                              className="horizon-badge-remove"
                              aria-label={`Unassign from ${horizonLabel(h)}`}
                              title={`Remove from ${horizonLabel(h)}`}
                              onClick={() => handleUnassignHorizonClick(group, h)}
                            >
                              ×
                            </button>
                          </span>
                        ))
                      ) : group._hasRestrictedAssignments ? (
                        <span className="no-horizons" title="Assigned to horizons you do not have access to">
                          Other horizons (not visible)
                        </span>
                      ) : (
                        <span className="no-horizons">No horizons assigned</span>
                      )}
                    </div>
                  </div>
                  <div className="card-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleEditClick(group)}
                      title="Edit ingestion source"
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleAssignClick(group)}
                      title="Assign to more horizons"
                    >
                      <i className="fas fa-plus"></i> Assign
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={() => handleWipeClick(group)}
                      title="Delete all ingested entries for this source (keeps the source configuration)"
                    >
                      <i className="fas fa-eraser"></i> Wipe data
                    </button>
                    {canDeleteSource(group) ? (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteClick(group)}
                        title="Delete ingestion source"
                      >
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled
                        title={deleteDisabledTitle(group)}
                        aria-label={deleteDisabledTitle(group)}
                      >
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && total > 0 && (
        <div className="ingestion-sources-pagination" aria-label="Ingestion sources pagination">
          <span className="ingestion-sources-pagination-summary">
            Showing {(page - 1) * SOURCES_PAGE_SIZE + 1}–{Math.min(page * SOURCES_PAGE_SIZE, total)} of {total}
          </span>
          <div className="ingestion-sources-pagination-buttons">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => loadIngestionGroups(page - 1)}
            >
              Previous
            </button>
            <span className="ingestion-sources-pagination-page">
              Page {page} of {Math.max(1, Math.ceil(total / SOURCES_PAGE_SIZE))}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page >= Math.max(1, Math.ceil(total / SOURCES_PAGE_SIZE))}
              onClick={() => loadIngestionGroups(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Ingestion Group Editor Modal */}
      {editorOpen && (
        <IngestionGroupEditor
          open={editorOpen}
          mode={editorMode}
          initial={editingGroup || EMPTY_GROUP_INITIAL}
          suffix={
            editorMode === 'create'
              ? activeSuffix || availableHorizons[0]?.suffix || 'zagger_product_help'
              : canonicalSuffix(editingGroup) || activeSuffix || availableHorizons[0]?.suffix || 'zagger_product_help'
          }
          onClose={handleEditorClose}
          onSaved={handleEditorSaved}
        />
      )}

      <PostgresIngestWizard
        open={postgresWizardOpen}
        suffix={activeSuffix || availableHorizons[0]?.suffix || ''}
        onClose={() => setPostgresWizardOpen(false)}
        onComplete={() => {
          loadIngestionGroups();
        }}
      />

      {/* Wipe data confirmation */}
      {wipeConfirmOpen && (
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Wipe ingested data?</h2>
              <span className="close" onClick={closeWipeConfirm}>&times;</span>
            </div>
            <div className="modal-body">
              <p>
                Remove all ingested entries for <strong>{wipingGroup?.name}</strong> on horizon{' '}
                <code>{canonicalSuffix(wipingGroup)}</code>?
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-triangle" /> The ingestion source and its settings are kept.
                Prompt outputs and embeddings tied to deleted entries are removed with the entries.
              </p>
              {supportsSchemaWipe(wipingGroup) ? (
                <label className="ingestion-wipe-schema-option">
                  <span className="ingestion-wipe-schema-row">
                    <input
                      type="checkbox"
                      checked={wipeSchema}
                      onChange={(e) => setWipeSchema(e.target.checked)}
                      disabled={wiping}
                    />
                    <span className="ingestion-wipe-schema-label">Wipe the schema?</span>
                  </span>
                  <span className="ingestion-wipe-schema-hint">
                    Clears auto-generated columns so they can be rebuilt on the next ingest
                    {wipingGroup?.config?.type === 'api_source' ? ' (API JSON columns)' : ' (Drive directory depth columns)'}.
                  </span>
                </label>
              ) : (
                <p className="ingestion-wipe-schema-hint">
                  Schema wipe is only available for API sources with generated JSON columns or Drive folders with directory depth columns.
                </p>
              )}
              <div className="ingestion-wipe-confirm-field">
                <label htmlFor="ingestion-wipe-confirm-text">
                  Type <strong>{WIPE_CONFIRM_WORD}</strong> to confirm
                </label>
                <input
                  id="ingestion-wipe-confirm-text"
                  type="text"
                  className="form-control"
                  value={wipeConfirmText}
                  onChange={(e) => setWipeConfirmText(e.target.value)}
                  placeholder={WIPE_CONFIRM_WORD}
                  disabled={wiping}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={wiping}
                onClick={closeWipeConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-warning"
                disabled={wiping || !wipeConfirmReady}
                onClick={handleWipeConfirm}
              >
                {wiping ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Wiping…
                  </>
                ) : (
                  <>
                    <i className="fas fa-eraser" /> Wipe data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Confirm Delete</h2>
              <span className="close" onClick={() => setDeleteConfirmOpen(false)}>&times;</span>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the ingestion source <strong>{deletingGroup?.name}</strong>?</p>
              <p className="warning-text">
                <i className="fas fa-exclamation-triangle"></i> This will permanently delete the ingestion group and all associated data.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
              >
                <i className="fas fa-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Horizon Assignment Modal */}
      {assignModalOpen && (
        <HorizonAssignmentModal
          open={assignModalOpen}
          ingestionGroup={assigningGroup}
          suffix={canonicalSuffix(assigningGroup)}
          onClose={handleAssignClose}
          onSaved={handleAssignSaved}
        />
      )}

      {/* Unassign horizon confirmation */}
      {unassignConfirm && (
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Remove horizon assignment?</h2>
              <span
                className="close"
                onClick={() => !unassigning && setUnassignConfirm(null)}
              >
                &times;
              </span>
            </div>
            <div className="modal-body">
              <p>
                Remove <strong>{unassignConfirm.group?.name}</strong> from data horizon{' '}
                <strong>{horizonLabel(unassignConfirm.horizonSuffix)}</strong>?
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-triangle" /> The source will no longer appear in that
                horizon. Other horizon assignments are unchanged.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={unassigning}
                onClick={() => setUnassignConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={unassigning}
                onClick={handleUnassignConfirm}
              >
                {unassigning ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Removing…
                  </>
                ) : (
                  <>
                    <i className="fas fa-times" /> Remove assignment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IngestionCRUD;
